// Local mirror of the employee's leads and tasks, so the module reads instantly
// and works with no signal. Backed by IndexedDB via `idb`.
//
//   leads / tasks  — the records, keyed by server id
//   meta           — small key/value (the sync cursor)
//   outbox         — edits made offline, awaiting push (Phase 3)
import { openDB } from 'idb';

const DB_NAME = 'dfc-offline';
const DB_VERSION = 1;

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains('leads')) db.createObjectStore('leads', { keyPath: 'id' });
    if (!db.objectStoreNames.contains('tasks')) db.createObjectStore('tasks', { keyPath: 'id' });
    if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
    if (!db.objectStoreNames.contains('outbox')) {
      db.createObjectStore('outbox', { keyPath: 'localId', autoIncrement: true });
    }
  },
});

export async function putRecords(store, records) {
  if (!records || !records.length) return;
  const db = await dbPromise;
  const tx = db.transaction(store, 'readwrite');
  await Promise.all(records.map((r) => tx.store.put(r)));
  await tx.done;
}

export async function getAllRecords(store) {
  const db = await dbPromise;
  return db.getAll(store);
}

export async function getMeta(key, fallback = null) {
  const db = await dbPromise;
  const row = await db.get('meta', key);
  return row ? row.value : fallback;
}

export async function setMeta(key, value) {
  const db = await dbPromise;
  await db.put('meta', { key, value });
}

// ── Outbox (used from Phase 3) ────────────────────────────────────────────────
export async function queueChange(change) {
  const db = await dbPromise;
  return db.add('outbox', { ...change, queuedAt: new Date().toISOString() });
}

export async function getOutbox() {
  const db = await dbPromise;
  return db.getAll('outbox');
}

export async function clearOutboxItems(localIds) {
  if (!localIds || !localIds.length) return;
  const db = await dbPromise;
  const tx = db.transaction('outbox', 'readwrite');
  await Promise.all(localIds.map((id) => tx.store.delete(id)));
  await tx.done;
}
